import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { useStorage } from "app/custom-hooks/storage/useStorage";
import { NewBadge } from "app/pages/sales-intelligence/common-components/badge/NewBadge";
import {
    INDUSTRY_TAXONOMY_DEMO_ID,
    INDUSTRY_TAXONOMY_KB_ARTICLE_LINK,
} from "app/pages/sales-intelligence/modules/industry/constants";
import { industryStorage } from "app/pages/sales-intelligence/modules/industry/services/storage";
import industryTrackingService from "app/pages/sales-intelligence/modules/industry/services/tracking/trackingService";
import industryI18nKeys from "app/pages/sales-intelligence/modules/industry/utils/i18n";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { IndustryTaxonomyBannerIllustration } from "./IndustryTaxonomyBannerIllustration";
import { StyledBannerCloseButton, StyledBannerTextColumn, StyledBannerWrapper, StyledLearnMoreLink } from "./styles";

/**
 * Home-page announcement for an account that has already been opted into the industry taxonomy. Mount it
 * through `IndustryTaxonomyHomeBanner`, which owns that condition — this component does not re-check it.
 *
 * Dismissal is per browser rather than per account: a second `/account-settings` key would add a second serial
 * fetch to the SI root resolve, which ADR-0013 rules out.
 */
export const IndustryTaxonomyLaunchBanner = () => {
    const { translate } = useTranslate();

    const [isDismissed, setIsDismissed] = useStorage<boolean>(industryStorage.launchBannerDismissed);

    if (isDismissed) {
        return null;
    }

    const handleDismiss = (): void => {
        industryTrackingService.trackLaunchBannerDismiss();
        setIsDismissed(true);
    };

    const handleTakeALook = (): void => {
        industryTrackingService.trackLaunchBannerCtaClick();

        // The demo id doubles as "open the taxonomy card on arrival": lead generation resolves it into an
        // expansion rather than a demo session for an opted-in user, so no second param is minted for it.
        void siNavigator.goToAdvancedSearchPage(undefined, { demo: INDUSTRY_TAXONOMY_DEMO_ID });
    };

    const learnMoreLabel = translate(industryI18nKeys.launchBannerLearnMore);

    return (
        <StyledBannerWrapper
            gap="24px"
            alignItems="center"
            justifyContent="space-between"
            data-automation="industry-taxonomy-launch-banner"
        >
            <StyledBannerTextColumn gap="24px">
                <Stack gap="8px">
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        <Text
                            fontVariant="h5_2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate(industryI18nKeys.launchBannerTitle)}
                        </Text>

                        <NewBadge />
                    </Flex>

                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[500]}
                        data-automation="industry-taxonomy-launch-banner-description"
                    >
                        {translate(industryI18nKeys.launchBannerDescription)}{" "}
                        <StyledLearnMoreLink
                            url={INDUSTRY_TAXONOMY_KB_ARTICLE_LINK}
                            label={learnMoreLabel}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-automation="industry-taxonomy-launch-banner-learn-more"
                        >
                            {learnMoreLabel}
                        </StyledLearnMoreLink>
                    </Text>
                </Stack>

                <IconButton
                    type="primary"
                    buttonHtmlType="button"
                    width="max-content"
                    iconName="arrow-right"
                    iconSize="12px"
                    placement="right"
                    onClick={handleTakeALook}
                    data-automation="industry-taxonomy-launch-banner-cta"
                >
                    {translate(industryI18nKeys.launchBannerCta)}
                </IconButton>
            </StyledBannerTextColumn>

            <IndustryTaxonomyBannerIllustration dataAutomation="industry-taxonomy-launch-banner-illustration" />

            <Tooltip
                content={translate(industryI18nKeys.launchBannerClose)}
                placement="left"
                enabled
            >
                <StyledBannerCloseButton
                    type="flat"
                    iconName="clear"
                    iconSize="16px"
                    onClick={handleDismiss}
                    data-automation="industry-taxonomy-launch-banner-close"
                />
            </Tooltip>
        </StyledBannerWrapper>
    );
};
